import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getEmailFromRecipient, removeTime } from '../helpers';
import {
    FilterOption,
    ScheduledDashboardEmail,
    ScheduledDashboardEmailRecipient,
} from '../types';
import validators from 'helpers/validators';

type ValidationErrors = {
    firstSentDate?: string;
    recipients?: string;
};

export const useScheduledEmailValidation = (
    scheduledEmail: ScheduledDashboardEmail,
    initialFirstSentDate: string,
    possibleRecipients: ScheduledDashboardEmailRecipient[],
) => {
    const today = removeTime(new Date()).getTime();

    const isValidDate = useCallback(
        (dateStr: string) => {
            const selected = removeTime(new Date(dateStr)).getTime();
            return dateStr === initialFirstSentDate || selected >= today;
        },
        [initialFirstSentDate, today],
    );

    const errors: ValidationErrors = useMemo(() => {
        const errs: ValidationErrors = {};

        // Check if the first sent date is valid
        if (!isValidDate(scheduledEmail.firstSentDate)) {
            errs.firstSentDate = 'sendDashboard.futureDate';
        }

        // Check if recipients are valid
        if (
            scheduledEmail.recipients.length === 0 ||
            !scheduledEmail.recipients.every((recipient) =>
                validators.isEmail(getEmailFromRecipient(recipient)),
            )
        ) {
            errs.recipients = 'validators.isEmail';
        } else if (
            // When the filter option is set to DEFAULT_FILTERS_RECIPIENT_SCOPES,
            // ensure all recipients are valid internal users
            scheduledEmail.filterOption ===
                FilterOption.DEFAULT_FILTERS_RECIPIENT_SCOPES &&
            !scheduledEmail.recipients.every((recipient) =>
                possibleRecipients.some(
                    (user) => user.recipientId === recipient.recipientId,
                ),
            )
        ) {
            errs.recipients = 'sendDashboard.onlyInternalRecipients';
        }

        return errs;
    }, [scheduledEmail, isValidDate, possibleRecipients]);

    return {
        errors,
        isValid: Object.keys(errors).length === 0,
        isValidDate,
    };
};

/**
 * Custom hook to determine if there are pending changes in the scheduled email form.
 * It compares the current scheduled email with the previous one to detect changes.
 * Only active when `isEditing` is true to disable or not the confirm button.
 */
export const usePendingScheduledEmailChanges = (
    scheduledEmail: ScheduledDashboardEmail,
    isEditing: boolean,
): boolean => {
    const [hasPendingChanges, setHasPendingChanges] = useState(!isEditing);
    const previousEmail = useRef(scheduledEmail);

    useEffect(() => {
        if (!isEditing) return;

        const hasChanged =
            JSON.stringify(previousEmail.current) !==
            JSON.stringify(scheduledEmail);
        setHasPendingChanges(hasChanged);
    }, [isEditing, scheduledEmail]);

    return hasPendingChanges;
};
